import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField,
    Divider, IconButton, CircularProgress, MenuItem,
    useTheme, useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import { registrarRotuloDeRota } from '../services/navegacao';
import { format, parseISO, isValid } from 'date-fns';
import { isVisualizador } from '../services/auth';
import { toastSuccess, toastWarning, toastError, alertValidacao, confirmAcao } from '../services/alerts';
import { buscarCep, cepCompleto, mensagemFalhaCep } from '../services/cep';

export default function PacienteCadastro() {
    const soLeitura = isVisualizador();
    const navigate = useNavigate();
    const { id } = useParams(); 
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!!id);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [paciente, setPaciente] = useState({
        nomeCompleto: '',
        cpf: '',
        rg: '',
        email: '',
        telefone: '',
        dataNascimento: '',
        profissao: '',
        nacionalidade: '',
        estadoCivil: '',
        nomeContatoEmergencia: '',
        telefoneContatoEmergencia: '',
        endereco: {
            cep: '',
            logradouro: '',
            numero: '',
            complemento: '',
            bairro: '',
            cidade: '',
            uf: ''
        }
    });

    useEffect(() => {
        if (id) {
            const carregarPaciente = async () => {
                try {
                    const response = await api.get(`/pacientes/${id}`);
                    const dados = response.data;

                    // Alimenta a trilha de navegação com o nome real do paciente
                    // (Pacientes › Maria › Cadastro), em vez de "#12".
                    if (dados?.nomeCompleto) {
                        registrarRotuloDeRota(`paciente:${id}`, dados.nomeCompleto);
                    }
                    setPaciente({
                        ...dados,
                        cpf: dados.cpf ? formatarCPF(dados.cpf) : '',
                        rg: dados.rg ? formatarRG(dados.rg) : '',
                        telefone: dados.telefone ? formatarTelefone(dados.telefone) : '',
                        telefoneContatoEmergencia: dados.telefoneContatoEmergencia ? formatarTelefone(dados.telefoneContatoEmergencia) : '',
                        dataNascimento: dados.dataNascimento ? formatarDataInput(dados.dataNascimento) : '',
                        endereco: dados.endereco ? {
                            cep: formatarCEP(dados.endereco.cep || ''),
                            logradouro: dados.endereco.logradouro || '',
                            numero: dados.endereco.numero || '',
                            complemento: dados.endereco.complemento || '',
                            bairro: dados.endereco.bairro || '',
                            cidade: dados.endereco.cidade?.nome || '',
                            uf: dados.endereco.cidade?.estado?.uf || ''
                        } : { cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' }
                    });
                } catch (err) {
                    console.error("Erro ao buscar dados do paciente", err);
                } finally {
                    setFetching(false);
                }
            };
            carregarPaciente();
        }
    }, [id]);

    const formatarCPF = (valor) => {
        if (!valor) return '';
        return valor.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})/, '$1-$2').replace(/(-\d{2})\d+?$/, '$1');
    };

    const formatarRG = (valor) => {
        if (!valor) return '';
        let v = valor.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (v.length > 2) v = v.substring(0, 2) + '.' + v.substring(2);
        if (v.length > 6) v = v.substring(0, 6) + '.' + v.substring(6);
        if (v.length > 10) v = v.substring(0, 10) + '-' + v.substring(10, 11);
        return v.substring(0, 12);
    };

    const formatarTelefone = (valor) => {
        if (!valor) return '';
        return valor.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4,5})(\d{4})/, '$1-$2').replace(/(-\d{4})\d+?$/, '$1');
    };
    
    const formatarCEP = (valor) => {
        if (!valor) return '';
        return valor.replace(/\D/g, '').replace(/^(\d{5})(\d)/, '$1-$2').substring(0, 9);
    };

    const formatarDataInput = (dataString) => {
        if (!dataString) return '';
        if (dataString.length > 10) {
            return dataString.substring(0, 10);
        }
        return dataString;
    };

    // Consulta via services/cep.js: lá ficam o timeout, o { erro: true } que o
    // ViaCEP devolve com HTTP 200 e as mensagens por motivo de falha.
    const handleBuscaCEP = async (event) => {
        if (!cepCompleto(event.target.value)) return;
        const resultado = await buscarCep(event.target.value);
        if (!resultado.ok) {
            if (resultado.motivo === 'nao_encontrado') toastWarning(mensagemFalhaCep(resultado.motivo));
            else toastError(mensagemFalhaCep(resultado.motivo));
            return;
        }
        setPaciente(prev => ({
            ...prev,
            endereco: {
                ...prev.endereco,
                logradouro: resultado.endereco.logradouro,
                bairro: resultado.endereco.bairro,
                cidade: resultado.endereco.localidade,
                uf: resultado.endereco.uf,
            }
        }));
    };

    const validarDados = () => {
        if (!paciente.nomeCompleto.trim()) return 'O Nome Completo é obrigatório.';
        if (paciente.cpf.replace(/\D/g, '').length !== 11) return 'CPF inválido.';
        const telLength = paciente.telefone.replace(/\D/g, '').length;
        if (telLength < 10) return 'Telefone inválido.';
        if (paciente.nomeContatoEmergencia && paciente.telefoneContatoEmergencia.replace(/\D/g, '').length < 10) return 'Telefone de Emergência inválido.';

        const end = paciente.endereco;
        if (end.cep && (!end.logradouro || !end.numero || !end.bairro || !end.cidade || !end.uf)) {
            return 'Ao informar o CEP, todos os campos de endereço se tornam obrigatórios (Logradouro, Número, Bairro, Cidade e UF).';
        }

        return null;
    };

    const solicitarSalvar = async () => {
        const erroMsg = validarDados();
        if (erroMsg) {
            alertValidacao(erroMsg);
            return;
        }
        const ok = await confirmAcao({
            title: 'Confirmar Alterações',
            text: 'Deseja confirmar e gravar os dados deste paciente?',
            confirmButtonText: 'Confirmar e Salvar',
            cancelButtonText: 'Rever Dados',
            icon: 'question',
        });
        if (ok) executarSalvar();
    };

    const executarSalvar = async () => {
        setLoading(true);

        try {
            const pacienteParaSalvar = {
                ...paciente,
                cpf: paciente.cpf.replace(/\D/g, ''),
                rg: paciente.rg.replace(/\D/g, ''),
                telefone: paciente.telefone.replace(/\D/g, ''),
                telefoneContatoEmergencia: paciente.telefoneContatoEmergencia.replace(/\D/g, ''),
                dataNascimento: paciente.dataNascimento || null,
                endereco: paciente.endereco.cep ? {
                    cep: paciente.endereco.cep.replace(/\D/g, ''),
                    logradouro: paciente.endereco.logradouro,
                    numero: paciente.endereco.numero,
                    complemento: paciente.endereco.complemento,
                    bairro: paciente.endereco.bairro,
                    nomeCidade: paciente.endereco.cidade,
                    ufEstado: paciente.endereco.uf
                } : null
            };

            let mensagemSucesso = '';
            if (id) {
                await api.put(`/pacientes/${id}`, pacienteParaSalvar);
                mensagemSucesso = 'Paciente atualizado com sucesso!';
            } else {
                await api.post('/pacientes', pacienteParaSalvar);
                mensagemSucesso = 'Paciente cadastrado com sucesso!';
            }

            toastSuccess(mensagemSucesso);
            setTimeout(() => navigate('/pacientes'), 1200);

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, px: isMobile ? 1 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/pacientes')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                    {soLeitura ? 'Visualizar Paciente' : id ? 'Editar Paciente' : 'Novo Paciente'}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                
                <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
                    Dados Pessoais
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField fullWidth label="Nome Completo" required value={paciente.nomeCompleto} onChange={(e) => setPaciente({...paciente, nomeCompleto: e.target.value})} disabled={soLeitura} />

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField fullWidth label="CPF" required value={paciente.cpf} onChange={(e) => setPaciente({...paciente, cpf: formatarCPF(e.target.value)})} inputProps={{ maxLength: 14 }} disabled={soLeitura} />
                        <TextField fullWidth label="RG" required value={paciente.rg} onChange={(e) => setPaciente({...paciente, rg: formatarRG(e.target.value)})} inputProps={{ maxLength: 12 }} disabled={soLeitura} />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField fullWidth label="E-mail" type="email" value={paciente.email} onChange={(e) => setPaciente({...paciente, email: e.target.value})} disabled={soLeitura} />
                        <TextField fullWidth label="Telefone" required value={paciente.telefone} onChange={(e) => setPaciente({...paciente, telefone: formatarTelefone(e.target.value)})} inputProps={{ maxLength: 15 }} disabled={soLeitura} />
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField fullWidth label="Data Nascimento" type="date" InputLabelProps={{ shrink: true }} value={paciente.dataNascimento} onChange={(e) => setPaciente({...paciente, dataNascimento: e.target.value})} disabled={soLeitura} />
                        <TextField fullWidth label="Profissão" value={paciente.profissao} onChange={(e) => setPaciente({...paciente, profissao: e.target.value})} disabled={soLeitura} />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField fullWidth label="Nacionalidade" value={paciente.nacionalidade || ''} onChange={(e) => setPaciente({...paciente, nacionalidade: e.target.value})} disabled={soLeitura} inputProps={{ maxLength: 50 }} />
                    </Box>

                    <TextField select fullWidth label="Estado Civil" value={paciente.estadoCivil} onChange={(e) => setPaciente({...paciente, estadoCivil: e.target.value})} disabled={soLeitura}>
                        <MenuItem value="SOLTEIRO">Solteiro(a)</MenuItem>
                        <MenuItem value="CASADO">Casado(a)</MenuItem>
                        <MenuItem value="DIVORCIADO">Divorciado(a)</MenuItem>
                        <MenuItem value="VIUVO">Viúvo(a)</MenuItem>
                        <MenuItem value="UNIAO_ESTAVEL">União Estável</MenuItem>
                    </TextField>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
                    Contato de Emergência
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField fullWidth label="Nome do Contato" value={paciente.nomeContatoEmergencia} onChange={(e) => setPaciente({...paciente, nomeContatoEmergencia: e.target.value})} disabled={soLeitura} />
                    <TextField fullWidth label="Telefone do Contato" value={paciente.telefoneContatoEmergencia} onChange={(e) => setPaciente({...paciente, telefoneContatoEmergencia: formatarTelefone(e.target.value)})} inputProps={{ maxLength: 15 }} disabled={soLeitura} />
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
                    Endereço
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            fullWidth
                            label="CEP"
                            value={paciente.endereco.cep}
                            onChange={(e) => setPaciente({ ...paciente, endereco: { ...paciente.endereco, cep: formatarCEP(e.target.value) } })}
                            onBlur={handleBuscaCEP}
                            inputProps={{ maxLength: 9 }}
                            helperText="Digite para buscar automaticamente"
                            disabled={soLeitura}
                        />
                        <TextField
                            fullWidth
                            label="Bairro"
                            required={!!paciente.endereco.cep}
                            value={paciente.endereco.bairro}
                            onChange={(e) => setPaciente({ ...paciente, endereco: { ...paciente.endereco, bairro: e.target.value } })}
                            disabled={soLeitura}
                        />
                    </Box>

                    <TextField
                        fullWidth
                        label="Logradouro"
                        required={!!paciente.endereco.cep}
                        value={paciente.endereco.logradouro}
                        onChange={(e) => setPaciente({ ...paciente, endereco: { ...paciente.endereco, logradouro: e.target.value } })}
                        disabled={soLeitura}
                    />

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Número"
                            required={!!paciente.endereco.cep}
                            value={paciente.endereco.numero}
                            onChange={(e) => setPaciente({ ...paciente, endereco: { ...paciente.endereco, numero: e.target.value } })}
                            disabled={soLeitura}
                        />
                        <TextField
                            fullWidth
                            label="Complemento"
                            placeholder="Apto, Bloco, Casa"
                            value={paciente.endereco.complemento}
                            onChange={(e) => setPaciente({ ...paciente, endereco: { ...paciente.endereco, complemento: e.target.value } })}
                            disabled={soLeitura}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            fullWidth
                            label="Cidade"
                            required={!!paciente.endereco.cep}
                            value={paciente.endereco.cidade}
                            onChange={(e) => setPaciente({ ...paciente, endereco: { ...paciente.endereco, cidade: e.target.value } })}
                            disabled={soLeitura}
                        />
                        <TextField
                            fullWidth
                            label="UF"
                            required={!!paciente.endereco.cep}
                            inputProps={{ maxLength: 2 }}
                            value={paciente.endereco.uf}
                            onChange={(e) => setPaciente({ ...paciente, endereco: { ...paciente.endereco, uf: e.target.value.toUpperCase() } })}
                            disabled={soLeitura}
                        />
                    </Box>
                </Box>



                <Divider sx={{ my: 4 }} />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" fullWidth={isMobile} onClick={() => navigate('/pacientes')} sx={{ color: '#666', borderColor: '#ccc' }}>
                        {soLeitura ? 'Voltar' : 'Cancelar'}
                    </Button>
                    {!soLeitura && (
                        <Button
                            variant="contained"
                            fullWidth={isMobile}
                            onClick={solicitarSalvar}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: { sm: 5 } }}
                        >
                            {id ? 'Atualizar Paciente' : 'Salvar Paciente'}
                        </Button>
                    )}
                </Box>
            </Paper>

        </Box>
    );
}